/**
 * newCopy entries for missing-data issues and the shared UNKNOWN fallback.
 */
import React from 'react';
import { CopyPack, Issue, IssueKind } from '../types';
import { ctxOf, HELP_ARTICLES, HelpArticle, SupportEmail, tt } from './shared';

const missingDataOf = (group: Issue[]) => (group[0].raw as { family: 'MISSING_DATA'; data: any }).data;

const unknownError__copy = {
  summary: (g: Issue[]) => tt(ctxOf(g), 'An unknown error has occurred'),
  whatCausedIt: (g: Issue[]) => tt(ctxOf(g), 'An uncommon and unexpected issue has occurred with the experiences listed below.'),
  howToFixIt: (g: Issue[]) => {
    const c = ctxOf(g);
    return (
      <>
        {tt(c, 'Contact')} <SupportEmail />{' '}
        {tt(
          c,
          "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page."
        )}
      </>
    );
  }
};

/** What `copyFor` renders for a kind that has no entry. A separate object
 * from the UNKNOWN entries below so tests can tell the two apart. */
export const fallbackCopy: CopyPack[IssueKind.UNKNOWN] = {
  category: 'Unknown error',
  ...unknownError__copy
};

export const miscNewCopy = {
  [IssueKind.MISSING_ACTOR]: {
    category: 'Missing supporting actor',
    summary: (g) => tt(ctxOf(g), `"{{ actor }}" is unassigned`, { actor: missingDataOf(g).actor?.name }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const actor = missingDataOf(g).actor?.name;
      return (
        <>
          {tt(c, 'The')} <strong>{actor}</strong>{' '}
          {tt(c, 'supporting actor is currently not assigned in the experience(s) listed below')}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>
            {tt(
              c,
              'Click the participants listed below to open up each experience, and check that the role is now assigned'
            )}
          </li>
          <li>
            {tt(c, 'If not, assign a user to this role')} <HelpArticle href={HELP_ARTICLES.changeStartDateOrManager} />
          </li>
          <li>{tt(c, 'This issue will automatically clear when you have assigned the missing actor')}</li>
        </ul>
      );
    }
  },
  [IssueKind.MISSING_VAR]: {
    category: 'Unknown error',
    ...unknownError__copy
  },
  [IssueKind.MISSING_DATA__UNKNOWN]: {
    category: 'Unknown error',
    ...unknownError__copy
  },
  [IssueKind.UNKNOWN]: {
    category: 'Unknown error',
    ...unknownError__copy
  }
} satisfies Partial<CopyPack>;
